import React from 'react';
import { Check, Package, Sparkles, Gift, Gem } from 'lucide-react';
import { Button } from './ui/button';
import { PLANS } from '../data/mock';
import { useQuote } from '../context/QuoteContext';
import SectionHeading from './SectionHeading';

const PLAN_ICON = { 'Gold Plan': Gift, 'Diamond Plan': Gem, 'Platinum Plan': Sparkles };

const Plans = () => {
  const { openQuote } = useQuote();
  return (
    <section id="plans" className="section-pad bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHeading
          eyebrow="Our Plans"
          title="Choose Your"
          accent="Perfect"
          tail="Plan"
          subtitle="Every plan includes a minimum of 100 personalized coconuts — A+ grade, fresh &amp; chilled, delivered to your venue."
        />

        <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {PLANS.map((p) => {
            const Icon = PLAN_ICON[p.name] || Sparkles;
            const highlight = p.highlight;
            return (
              <div
                key={p.name}
                className={`relative rounded-3xl p-7 border transition-all duration-300 flex flex-col ${
                  highlight
                    ? 'bg-blush border-pink-300 shadow-pink-glow md:-translate-y-6 md:scale-[1.02] z-10'
                    : 'bg-white border-pink-100 shadow-soft hover:-translate-y-1 hover:shadow-floating'
                }`}
                data-testid={`plan-card-${p.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {highlight && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-pink-brand text-white text-[11px] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg">
                    <Gem className="w-3.5 h-3.5" /> {p.badge}
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${highlight ? 'bg-white text-pink-brand' : 'bg-blush text-pink-brand'}`}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-2xl text-stone-900">{p.name}</h3>
                    <p className="text-sm text-stone-500">{p.subtitle}</p>
                  </div>
                </div>

                <p className="mt-5 text-stone-700 italic text-[15px]">"{p.description}"</p>

                <div className="mt-5 space-y-2.5">
                  {p.features.map((f) => (
                    <div key={f} className="flex items-start gap-3">
                      <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${highlight ? 'bg-pink-brand text-white' : 'bg-blush text-pink-brand'}`}>
                        <Check className="w-3 h-3" />
                      </span>
                      <span className="text-sm text-stone-700">{f}</span>
                    </div>
                  ))}
                </div>

                {p.note && (
                  <div className="mt-4 p-3 rounded-2xl bg-white/80 text-stone-700 text-xs border border-pink-100">
                    <strong className="text-pink-brand">Note:</strong> {p.note}
                  </div>
                )}

                <p className="mt-4 text-sm text-stone-600 flex-1">{p.footer}</p>

                <div className="mt-6 flex items-center gap-2 text-sm font-semibold text-pink-brand">
                  <Package className="w-4 h-4" /> {p.minimum}
                </div>

                <Button
                  onClick={() => openQuote({ plan_interest: p.name, source: `plans-${p.name.toLowerCase()}` })}
                  className={`w-full mt-5 rounded-full h-12 font-semibold ${
                    highlight
                      ? 'bg-pink-brand hover:bg-[#BE185D] text-white shadow-pink-glow'
                      : 'bg-stone-900 hover:bg-stone-800 text-white'
                  }`}
                  data-testid={`plan-${p.name.toLowerCase().replace(/\s+/g, '-')}-quote-button`}
                >
                  Get a Quote
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-sm text-stone-500 mt-10">
          All prices are indicative.{' '}
          <button
            type="button"
            onClick={() => openQuote({ source: 'plans-footer' })}
            className="text-pink-brand underline underline-offset-4 font-medium"
            data-testid="plans-footer-quote-link"
          >
            Contact us for a custom quote →
          </button>
        </p>
      </div>
    </section>
  );
};

export default Plans;
